import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GoogleLogo, AppleLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { App as CapacitorApp } from '@capacitor/app';

const API = process.env.REACT_APP_BACKEND_URL;
// Set this env var ONLY after Apple has verified your domain ownership.
// On native iOS we use the Capacitor plugin and don't need this.
const APPLE_WEB_ENABLED = !!process.env.REACT_APP_APPLE_SERVICES_ID;
const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID;
// iOS bundle ID — used as the audience when authenticating natively via Capacitor.
const APPLE_BUNDLE_ID = process.env.REACT_APP_APPLE_BUNDLE_ID || 'games.cyboard.app';

const isNativeIOS = () => {
  // Multiple fallbacks so the detection works even if @capacitor/core hasn't
  // finished initializing yet — checks the bridge global as a backstop.
  try {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') return true;
  } catch { /* ignore */ }
  try {
    if (typeof window !== 'undefined') {
      if (window.Capacitor && window.Capacitor.platform === 'ios') return true;
      if (window.webkit && window.webkit.messageHandlers) return true; // native iOS bridge
    }
  } catch { /* ignore */ }
  return false;
};

// True for ANY native build (iOS or Android).
const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

/**
 * Continue with Google
 *
 * On WEB: full-page redirect (existing flow).
 * On NATIVE: opens the OAuth URL inside an in-app browser via @capacitor/browser
 *   (SFSafariViewController on iOS, Chrome Custom Tab on Android). This keeps
 *   sign-in inside the app context to satisfy Apple Guideline 4. After Emergent
 *   redirects to our universal link cyboard.games/auth/google/callback, iOS hands
 *   control back to our app via the appUrlOpen listener, we close the browser,
 *   and the WebView navigates to /auth/google/callback to complete the session.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export function GoogleSignInButton() {
  // Wire universal-link AND Browser.browserPageLoaded listeners on native.
  // We listen on BOTH because:
  //   • appUrlOpen fires only when iOS/Android route the universal-link
  //     callback back to our app (requires Associated Domains entitlement).
  //   • browserPageLoaded fires every time the in-app SFSafariViewController /
  //     Chrome Custom Tab navigates. We check the URL ourselves so OAuth
  //     completes even before associated-domains are wired up at the apex.
  useEffect(() => {
    if (!isNative()) return;
    let urlSub, browserSub;

    const completeWithUrl = async (url) => {
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.close();
      } catch { /* may already be closed */ }
      try {
        const u = new URL(url);
        // Navigate the main WebView to the callback route + preserve token hash/query.
        window.location.href = `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
      } catch {
        window.location.href = url;
      }
    };

    (async () => {
      // a) Universal-link path
      try {
        urlSub = await CapacitorApp.addListener('appUrlOpen', (event) => {
          if (event?.url && event.url.includes('/auth/google/callback')) completeWithUrl(event.url);
        });
      } catch { /* ignore */ }

      // b) In-browser page-load path (works without associated-domains)
      try {
        const { Browser } = await import('@capacitor/browser');
        browserSub = await Browser.addListener('browserPageLoaded', (info) => {
          const url = info?.url || '';
          if (url.includes('/auth/google/callback')) completeWithUrl(url);
        });
      } catch { /* @capacitor/browser not in this native build */ }
    })();

    return () => {
      if (urlSub) { try { urlSub.remove(); } catch { /* ignore */ } }
      if (browserSub) { try { browserSub.remove(); } catch { /* ignore */ } }
    };
  }, []);

  const handleClick = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNative()) {
      // Native builds must use a stable universal-link URL configured at build time
      // (Apple/Google universal-link entitlements are bound to a fixed domain).
      const redirectUrl = process.env.REACT_APP_NATIVE_OAUTH_REDIRECT_URL;
      const oauthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: oauthUrl, presentationStyle: 'popover' });
      } catch (e) {
        toast.error('Could not open sign-in. Please try again.');
      }
    } else {
      const redirectUrl = window.location.origin + '/auth/google/callback';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.08] text-white text-sm font-medium transition-all"
      data-testid="google-signin-btn"
    >
      <GoogleLogo size={18} weight="bold" className="text-[#EA4335]" />
      Google
    </button>
  );
}

/**
 * Sign in with Apple.
 *  - On native iOS (inside the Capacitor app) — uses the native ASAuthorization sheet via
 *    @capacitor-community/apple-sign-in. No domain verification needed.
 *  - On the web — uses the AppleID JS SDK popup. Requires REACT_APP_APPLE_SERVICES_ID
 *    AND domain verification completed in developer.apple.com. Component returns null
 *    on web until that env var is set.
 */
export function AppleSignInButton() {
  const [loading, setLoading] = useState(false);
  const native = isNativeIOS();
  const visible = native || APPLE_WEB_ENABLED;

  useEffect(() => {
    if (native || !APPLE_WEB_ENABLED) return;
    if (window.AppleID) return;
    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => {
      try {
        window.AppleID.auth.init({
          clientId: APPLE_SERVICES_ID,
          scope: 'name email',
          redirectURI: window.location.origin + '/auth/apple/callback',
          usePopup: true,
        });
      } catch (e) { console.warn('AppleID.auth.init failed', e); }
    };
    document.body.appendChild(script);
  }, [native]);

  if (!visible) return null;

  const handleClick = async () => {
    setLoading(true);
    try {
      let identityToken, userName;

      if (native) {
        // ============ Native iOS Apple Sign-In ============
        // The audience baked into the identity token will be our iOS App ID
        // (e.g. games.cyboard.app), so the clientId here MUST match it.
        const result = await SignInWithApple.authorize({
          clientId: APPLE_BUNDLE_ID,
          scopes: 'email name',
        });
        identityToken = result?.response?.identityToken;
        if (!identityToken) throw new Error('Apple did not return an identity token');
        const givenName = result?.response?.givenName;
        const familyName = result?.response?.familyName;
        userName = [givenName, familyName].filter(Boolean).join(' ') || undefined;
      } else {
        // ============ Web Apple Sign-In ============
        if (!window.AppleID?.auth) {
          toast.error('Apple sign-in is loading… try again in a sec');
          setLoading(false);
          return;
        }
        const response = await window.AppleID.auth.signIn();
        identityToken = response?.authorization?.id_token;
        if (!identityToken) throw new Error('Apple did not return an identity token');
        userName = response?.user
          ? [response.user.name?.firstName, response.user.name?.lastName].filter(Boolean).join(' ')
          : undefined;
      }

      const { data } = await axios.post(
        `${API}/api/auth/apple`,
        { identity_token: identityToken, user_name: userName || undefined },
        { withCredentials: true }
      );
      // Persist tokens for native iOS (cookies are unreliable in WKWebView).
      try {
        if (data?.access_token) localStorage.setItem('cyb_at', data.access_token);
        if (data?.refresh_token) localStorage.setItem('cyb_rt', data.refresh_token);
      } catch { /* ignore */ }
      toast.success(`Welcome${data?.name ? `, ${data.name}` : ''}!`);
      window.location.href = '/';
    } catch (err) {
      if (err?.error !== 'popup_closed_by_user' && err?.code !== '1001') {
        toast.error(err?.response?.data?.detail || err?.message || 'Apple sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-white text-black hover:bg-[#f1f1f1] text-sm font-medium transition-all disabled:opacity-60"
      data-testid="apple-signin-btn"
    >
      <AppleLogo size={18} weight="fill" />
      {loading ? 'Signing in…' : 'Apple'}
    </button>
  );
}
